// LAP refonte Mes Objectifs — la table DataGrid historique devient une grille
// de CARTES AVEC PROGRESSION (choix produit) :
//   * section « Focus » en tête : les objectifs « Engagé » (3 maintenus
//     automatiquement par le back — ensure_focus_objectives), cartes sombres
//     mises en avant ;
//   * grille des autres objectifs en cartes claires ;
//   * chaque carte : domaine, nom, barre de progression (reached / cible),
//     échéance, statut, liaisons cliquables (roadmap / projet / liste / tâche /
//     événement) et actions (modifier / réussite / annuler).
// Le deep-link SmartFeed `?focus=<id>` surligne la carte ciblée (useFocusHighlight).
import React from 'react'
import {
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import LinkIcon from '@mui/icons-material/Link'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { convertToUserDateFormate } from 'utils/helpers'
import { useFocusHighlight } from 'utils/useFocusHighlight'

// Puces de statut — palette alignée sur statusPalette.js (Mes ToDo).
const STATUS_META = {
  Engagé: { key: 'statusEngaged', color: '#92400e', bg: '#fef3c7' },
  'En cours': { key: 'statusInProgress', color: '#1d4ed8', bg: '#dbeafe' },
  reussite: { key: 'statusAchieved', color: '#15803d', bg: '#dcfce7' },
  Annulé: { key: 'statusCancelled', color: '#b91c1c', bg: '#fee2e2' },
}

const LINK_TYPE_KEYS = {
  roadmap: 'linkTypeRoadmap',
  project: 'linkTypeProject',
  list: 'linkTypeList',
  task: 'linkTypeTask',
  event: 'linkTypeEvent',
}

function StatusChip({ status, t }) {
  const meta = STATUS_META[status]
  if (!meta) {
    return (
      <Chip size="small" label={t('mesObjectifs.notAvailable')} sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 600 }} />
    )
  }
  return (
    <Chip
      size="small"
      label={t(`mesObjectifs.${meta.key}`)}
      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }}
    />
  )
}

function ObjectifCard({ objectif, focus = false, highlighted = false, onEdit, onSetStatus, t, siteUser }) {
  const navigate = useNavigate()
  const progress = objectif.progress_percent
  const terminal = objectif.status === 'reussite' || objectif.status === 'Annulé'

  // Focus (Engagé) = carte sombre marine ; sinon carte blanche.
  const dark = focus
  const textColor = dark ? 'white' : '#0f172a'
  const subColor = dark ? 'rgba(255,255,255,0.7)' : '#64748b'

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderRadius: '12px',
        bgcolor: dark ? '#001b42' : 'white',
        border: highlighted
          ? '2px solid #f59e0b'
          : dark
            ? '1px solid rgba(255,255,255,0.18)'
            : '1px solid #e2e8f0',
        boxShadow: highlighted
          ? '0 0 0 4px rgba(245, 158, 11, 0.25)'
          : '0 1px 3px rgba(15, 23, 42, 0.08)',
        transition: 'box-shadow 0.2s',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Chip
          size="small"
          label={objectif.domain}
          sx={{
            bgcolor: dark ? 'rgba(255,255,255,0.12)' : '#f1f5f9',
            color: dark ? 'white' : '#475569',
            fontWeight: 600,
          }}
        />
        <StatusChip status={objectif.status} t={t} />
      </Box>

      <Typography sx={{ color: textColor, fontWeight: 700, lineHeight: 1.3 }}>
        {objectif.objectives_name || t('mesObjectifs.notAvailable')}
      </Typography>

      {/* Barre de progression : reached / cible (masquée si incalculable) */}
      <Box>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" sx={{ color: subColor }}>
            {t('mesObjectifs.progressLabel')}
          </Typography>
          <Typography variant="caption" sx={{ color: textColor, fontWeight: 600 }}>
            {progress != null
              ? `${objectif.reached ?? 0} / ${objectif.objectifs} (${progress}%)`
              : `${objectif.reached ?? '—'} / ${objectif.objectifs}`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress != null ? progress : 0}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: dark ? 'rgba(255,255,255,0.15)' : '#e2e8f0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              bgcolor: progress >= 100 ? '#22c55e' : dark ? '#f59e0b' : '#3b82f6',
            },
          }}
        />
      </Box>

      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
        <Typography variant="caption" sx={{ color: subColor }}>
          {t('mesObjectifs.dueDateColon')}{' '}
          <Box component="span" sx={{ color: textColor, fontWeight: 600 }}>
            {objectif.date_echeance
              ? convertToUserDateFormate(siteUser.user, objectif.date_echeance)
              : t('mesObjectifs.notAvailable')}
          </Box>
        </Typography>
        <Typography variant="caption" sx={{ color: subColor }}>
          {objectif.periodicity}
        </Typography>
      </Box>

      {/* Liaisons cliquables vers les éléments concrets */}
      {(objectif.links || []).length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {objectif.links.map((link) => (
            <Tooltip
              key={link.id}
              title={t(`mesObjectifs.${LINK_TYPE_KEYS[link.target_type] || 'linkTypeTask'}`)}
            >
              <Chip
                size="small"
                icon={<LinkIcon sx={{ fontSize: 14 }} />}
                label={link.label}
                onClick={() => link.route && navigate(link.route)}
                sx={{
                  maxWidth: 180,
                  bgcolor: dark ? 'rgba(255,255,255,0.12)' : '#eff6ff',
                  color: dark ? 'white' : '#1d4ed8',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end" mt="auto">
        <Tooltip title={t('mesObjectifs.editObjective')}>
          <IconButton size="small" onClick={() => onEdit(objectif)} sx={{ color: subColor }}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!terminal && (
          <>
            <Tooltip title={t('mesObjectifs.markAchieved')}>
              <IconButton
                size="small"
                onClick={() => onSetStatus(objectif, 'reussite')}
                sx={{ color: '#22c55e' }}
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('mesObjectifs.cancel')}>
              <IconButton
                size="small"
                onClick={() => onSetStatus(objectif, 'Annulé')}
                sx={{ color: '#ef4444' }}
              >
                <CancelOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  )
}

function ListObjectifs({ objectifs, onEdit, onSetStatus }) {
  const { t } = useTranslation('layout')
  const siteUser = useSelector((store) => store.user)
  // Deep-link SmartFeed (/mesobjectifs?focus=<id>) — surligne la carte ciblée.
  const { isFocused } = useFocusHighlight()

  const results = objectifs.results || []
  const focusObjectifs = results.filter((o) => o.status === 'Engagé')
  const others = results.filter((o) => o.status !== 'Engagé')

  return (
    <Box pb={4}>
      {/* Section Focus — les 3 objectifs « Engagé » maintenus par le système */}
      {focusObjectifs.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            🎯 {t('mesObjectifs.focusTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
            {t('mesObjectifs.focusSubtitle')}
          </Typography>
          <Grid container spacing={2}>
            {focusObjectifs.map((o) => (
              <Grid item xs={12} sm={6} md={4} key={o.id}>
                <ObjectifCard
                  objectif={o}
                  focus
                  highlighted={isFocused(o.id)}
                  onEdit={onEdit}
                  onSetStatus={onSetStatus}
                  t={t}
                  siteUser={siteUser}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1.5 }}>
        {t('mesObjectifs.pageTitle')} ({objectifs.count ?? results.length})
      </Typography>
      {others.length === 0 && focusObjectifs.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'white',
            borderRadius: '12px',
            color: '#64748b',
          }}
        >
          {t('mesObjectifs.emptyList')}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {others.map((o) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={o.id}>
              <ObjectifCard
                objectif={o}
                highlighted={isFocused(o.id)}
                onEdit={onEdit}
                onSetStatus={onSetStatus}
                t={t}
                siteUser={siteUser}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}

export default ListObjectifs
